import { useState, useCallback, useRef } from "react";

const defaultConfig = {
  confirmationMessage: "Are you sure you want to delete this item?",
  errorMessage: "Failed to delete. Please try again.",
  dialogTitle: "Confirm Delete",
  confirmButtonText: "Delete",
  cancelButtonText: "Cancel",
  dialogWidth: "max-w-md",
  closeOnOverlayClick: true,
};

export const useDelete = (config = {}) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState(null);
  const [isConfirming, setIsConfirming] = useState(false);
  const [pendingArgs, setPendingArgs] = useState([]);
  const deferredRef = useRef({});

  const mergedConfig = { ...defaultConfig, ...config };

  if (!mergedConfig.DialogComponent) {
    throw new Error("DialogComponent is required in useDelete config");
  }

  const handleDelete = (...args) => {
    setIsConfirming(true);
    setPendingArgs(args);
    return new Promise((resolve, reject) => {
      deferredRef.current = { resolve, reject };
    });
  };

  const executeDelete = async () => {
    setIsConfirming(false);
    setIsDeleting(true);
    setError(null);

    try {
      const result = await mergedConfig.deleteApi(...pendingArgs);
      mergedConfig.onSuccess?.(result, ...pendingArgs);
      deferredRef.current.resolve(result);
      return result;
    } catch (err) {
      setError(err);
      mergedConfig.onError?.(err, ...pendingArgs);
      if (!mergedConfig.onError) {
        alert(mergedConfig.errorMessage);
      }
      deferredRef.current.reject(err);
      throw err;
    } finally {
      setIsDeleting(false);
    }
  };

  const handleConfirm = () => executeDelete();
  const handleCancel = () => {
    setIsConfirming(false);
    deferredRef.current.reject(new Error("User cancelled"));
  };

  const DeleteConfirmationDialog = () => (
    <mergedConfig.DialogComponent
      isOpen={isConfirming}
      onClose={handleCancel}
      title={mergedConfig.dialogTitle}
      width={mergedConfig.dialogWidth}
      closeOnOverlayClick={mergedConfig.closeOnOverlayClick}
    >
      <div className="p-4">
        <p className="text-gray-600 mb-4">{mergedConfig.confirmationMessage}</p>
        <div className="flex justify-end space-x-3">
          <button
            onClick={handleCancel}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 rounded-lg hover:bg-gray-100 transition-colors"
          >
            {mergedConfig.cancelButtonText}
          </button>
          <button
            onClick={handleConfirm}
            className={`px-4 py-2 text-white rounded-lg transition-colors ${
              isDeleting
                ? "bg-red-400 cursor-wait"
                : "bg-red-600 hover:bg-red-700"
            }`}
            disabled={isDeleting}
          >
            {isDeleting ? "Deleting..." : mergedConfig.confirmButtonText}
          </button>
        </div>
      </div>
    </mergedConfig.DialogComponent>
  );

  const resetError = useCallback(() => setError(null), []);

  return {
    handleDelete,
    isDeleting,
    error,
    resetError,
    DeleteConfirmationDialog,
  };
};
